import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LangContext";
import { BLOG } from "@/constants/testIds";
import { ArrowRight, X } from "lucide-react";
import { STATIC_BLOG_POSTS } from "@/blogData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const { lang, t } = useLang();
  const [posts, setPosts] = useState(STATIC_BLOG_POSTS);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setPosts(r.data);
      })
      .catch(() => {
        // Backend unreachable (static deployment) — keep bundled fallback data
      });
  }, []);

  const fmtDate = (s) =>
    new Date(s).toLocaleDateString(lang === "ro" ? "ro-RO" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <section id="blog" className="py-24 md:py-32 bg-[#F3EFE9]/60 relative">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
          <div className="lg:col-span-6">
            <p className="overline">{t.blog.overline}</p>
            <h2 className="display mt-5 text-4xl md:text-5xl lg:text-6xl text-[#1A0010]">
              {t.blog.title}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-14">
            <img
              src="https://images.unsplash.com/photo-1600181982553-ce7d36051c01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwbGF3JTIwYm9va3MlMjBsaWJyYXJ5fGVufDB8fHx8MTc4MTI4NzQxNnww&ixlib=rb-4.1.0&q=85"
              alt=""
              className="editorial-img w-full h-56 object-cover"
            />
          </div>
        </div>

        <div
          data-testid={BLOG.list}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#D4AF37]/30"
        >
          {posts.map((p) => (
            <article
              key={p.slug}
              data-testid={BLOG.card(p.slug)}
              className="p-7 lg:p-8 border-r border-b border-[#D4AF37]/30 group flex flex-col hover:bg-[#FBF9F6] transition-colors"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="overline !text-[#66023C]">
                  {lang === "ro" ? p.category_ro : p.category_en}
                </span>
                <span className="text-xs text-[#4D3D46]/70">
                  {p.read_minutes} {t.blog.minutes}
                </span>
              </div>
              <h3 className="serif text-2xl text-[#1A0010] font-light leading-tight min-h-[5rem]">
                {lang === "ro" ? p.title_ro : p.title_en}
              </h3>
              <p className="mt-4 text-sm text-[#4D3D46] leading-relaxed flex-grow">
                {lang === "ro" ? p.excerpt_ro : p.excerpt_en}
              </p>
              <div className="mt-6 pt-5 border-t border-[#D4AF37]/25 flex items-center justify-between">
                <span className="text-xs text-[#4D3D46]/70">{fmtDate(p.date)}</span>
                <button
                  data-testid={BLOG.open(p.slug)}
                  onClick={() => setActive(p)}
                  className="text-[11px] tracking-[0.22em] uppercase font-medium text-[#66023C] inline-flex items-center gap-2 group-hover:gap-3 transition-all"
                >
                  {t.blog.readMore} <ArrowRight size={12} strokeWidth={1.5} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Modal */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-[#1A0010]/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-10 overflow-y-auto"
          onClick={() => setActive(null)}
          data-testid="blog-modal"
        >
          <div
            className="bg-[#FBF9F6] max-w-3xl w-full p-8 md:p-14 relative my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 text-[#1A0010] hover:text-[#66023C]"
              data-testid="blog-modal-close"
            >
              <X size={22} />
            </button>
            <p className="overline !text-[#66023C]">
              {lang === "ro" ? active.category_ro : active.category_en}
            </p>
            <h3 className="display mt-4 text-3xl md:text-5xl text-[#1A0010] font-light leading-tight">
              {lang === "ro" ? active.title_ro : active.title_en}
            </h3>
            <p className="mt-4 text-xs text-[#4D3D46]/70">
              {fmtDate(active.date)} · {active.read_minutes} {t.blog.minutes}
            </p>
            <div className="hairline-strong w-20 my-8" />
            <div className="serif text-lg text-[#1A0010] leading-relaxed whitespace-pre-line font-light">
              {lang === "ro" ? active.content_ro : active.content_en}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Blog;
